/**
 * Page invités plein écran
 * Route : /mon-organisation/plan-de-table/invites
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, Plus, Download, Upload, MoreHorizontal, Users } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useAuth }       from '../context/AuthContext';
import { useInvites }    from '../hooks/useInvites';
import StatusDot         from '../components/plan-table/StatusDot';
import GuestModal        from '../components/plan-table/GuestModal';
import ImportModal       from '../components/plan-table/ImportModal';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ─── Couleurs stat cards ──────────────────────────────────────────────────────
const STAT_COLORS = {
  total:    '#D4AF37',
  yes:      '#16A34A',
  no:       '#EF4444',
  pending:  '#F59E0B',
  placed:   '#0F766E',
  plus_one: '#374151',
};

const DIET_LABELS = {
  standard:'Aucun', vegetarian:'Végé', vegan:'Vegan',
  halal:'Halal', kid:'Enfant', other:'Autre',
};

export default function GuestsPage() {
  const { user, token } = useAuth();
  const [planToken, setPlanToken] = useState(null);

  // Récupérer le token du plan
  useEffect(() => {
    if (!user || !token) return;
    fetch(`${BACKEND}/api/mon-plan-de-table`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { if (d.plans?.[0]?.token) setPlanToken(d.plans[0].token); })
      .catch(() => {});
  }, [user, token]);

  const {
    plan, invites, loading, groups, segments, mainSeg,
    fetchAll, createInvite, updateInvite, deleteInvite,
    cycleStatus, bulkImport, exportCSV,
  } = useInvites(planToken, token);

  useEffect(() => { if (planToken) fetchAll(); }, [planToken, fetchAll]);

  // ─── Filtres / recherche ────────────────────────────────────────────────
  const [search,      setSearch]      = useState('');
  const [filterGroup, setFilterGroup] = useState('__all__');
  const [filterStatus,setFilterStatus]= useState('__all__');

  const filtered = useMemo(() => {
    let list = invites;
    if (search)                  list = list.filter(i => i.nom.toLowerCase().includes(search.toLowerCase()));
    if (filterGroup !== '__all__') list = list.filter(i => i.group_id === filterGroup);
    if (filterStatus !== '__all__') {
      if (filterStatus === 'placed')    list = list.filter(i => i.table_id);
      else if (filterStatus === 'unplaced') list = list.filter(i => !i.table_id);
      else list = list.filter(i => mainSeg && i.seg_status?.[mainSeg.id] === filterStatus);
    }
    return list;
  }, [invites, search, filterGroup, filterStatus, mainSeg]);

  // ─── Stats ──────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:    invites.length,
    yes:      invites.filter(i => mainSeg && i.seg_status?.[mainSeg.id] === 'yes').length,
    no:       invites.filter(i => mainSeg && i.seg_status?.[mainSeg.id] === 'no').length,
    pending:  invites.filter(i => mainSeg && i.seg_status?.[mainSeg.id] === 'pending').length,
    placed:   invites.filter(i => i.table_id).length,
    plus_one: invites.filter(i => i.plus_one?.name).length,
  }), [invites, mainSeg]);

  // ─── Modals ─────────────────────────────────────────────────────────────
  const [guestModal,  setGuestModal]  = useState({ open:false, guest:null });
  const [importModal, setImportModal] = useState(false);

  const groupMap = useMemo(() => {
    const m = {}; groups.forEach(g => { m[g.id] = g; }); return m;
  }, [groups]);

  // ─── Actions ─────────────────────────────────────────────────────────────
  const handleSaveGuest = async (data) => {
    if (guestModal.guest) {
      await updateInvite(guestModal.guest.id, data);
    } else {
      await createInvite(data);
    }
  };

  const handleDelete = async (invite) => {
    if (!window.confirm(`Supprimer « ${invite.nom} » ?`)) return;
    try {
      await deleteInvite(invite.id);
      toast.success('Invité supprimé');
    } catch { toast.error('Erreur suppression'); }
  };

  const handleDuplicate = async (invite) => {
    try {
      await createInvite({
        nom: invite.nom + ' (2)',
        email: null, telephone: invite.telephone,
        group_id: invite.group_id, age: invite.age,
        diet: invite.diet, note: invite.note,
        plus_one: invite.plus_one ? { ...invite.plus_one } : null,
      });
      toast.success('Invité dupliqué');
    } catch { toast.error('Erreur duplication'); }
  };

  const handleExport = async () => {
    try {
      await exportCSV(plan?.nom_evenement || 'invites');
      toast.success('Export CSV téléchargé');
    } catch (e) { toast.error(e.message); }
  };

  if (!user) return (
    <div style={{ padding:40, textAlign:'center', fontFamily:"'Montserrat',sans-serif" }}>
      <Link to="/connexion" style={{ color:'#D4AF37' }}>Se connecter</Link>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg,#FEFAF5)', fontFamily:"'Montserrat',sans-serif" }}>
      {/* Nav */}
      <div style={{ background:'#0B1E20', borderBottom:'2px solid #D4AF37', padding:'0 20px', height:48, display:'flex', alignItems:'center', gap:12 }}>
        <Link to="/mon-organisation/plan-de-table/editeur"
          style={{ color:'#D4AF37', display:'flex', alignItems:'center', gap:4, textDecoration:'none', fontSize:12, fontWeight:600 }}>
          <ArrowLeft size={14} /> Éditeur
        </Link>
        <span style={{ color:'rgba(255,255,255,.3)' }}>|</span>
        <span style={{ color:'#FFFFFF', fontSize:13, fontWeight:700 }}>
          {plan?.nom_evenement || 'Invités'}
        </span>
      </div>

      <div style={{ maxWidth:1200, margin:'0 auto', padding:'24px 16px' }}>

        {/* ─── Stat cards ─── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:10, marginBottom:20 }}
          className="stats-grid">
          {[
            { key:'total',   label:'Invités',     color:STAT_COLORS.total   },
            { key:'yes',     label:'Confirmés',   color:STAT_COLORS.yes     },
            { key:'no',      label:'Refusés',     color:STAT_COLORS.no      },
            { key:'pending', label:'En attente',  color:STAT_COLORS.pending },
            { key:'placed',  label:'Placés',      color:STAT_COLORS.placed  },
            { key:'plus_one',label:'Avec +1',     color:STAT_COLORS.plus_one},
          ].map(s => (
            <div key={s.key}
              style={{ background:'#FFFFFF', borderRadius:12, padding:'14px 10px', textAlign:'center',
                border:'1px solid #F3F4F6', boxShadow:'0 1px 4px rgba(0,0,0,.05)' }}>
              <p style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:28,
                color:s.color, margin:0, lineHeight:1 }}>
                {stats[s.key]}
              </p>
              <p style={{ fontSize:9, fontWeight:700, textTransform:'uppercase',
                letterSpacing:'0.08em', color:'#9CA3AF', margin:'4px 0 0' }}>
                {s.label}
              </p>
            </div>
          ))}
        </div>

        {/* ─── Toolbar ─── */}
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap',
          marginBottom:16, position:'sticky', top:48, zIndex:30,
          background:'var(--bg,#FEFAF5)', padding:'8px 0', borderBottom:'1px solid #F3F4F6' }}>

          {/* Search */}
          <div style={{ position:'relative', maxWidth:220 }}>
            <Search size={13} style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', color:'#9CA3AF' }} />
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Rechercher…"
              style={{ padding:'6px 8px 6px 26px', borderRadius:7, fontSize:12,
                border:'1px solid #D1D5DB', outline:'none', width:220,
                fontFamily:"'Montserrat',sans-serif" }} />
          </div>

          {/* Filtre groupe */}
          <Select value={filterGroup} onValueChange={setFilterGroup}>
            <SelectTrigger style={{ width:160, fontSize:12, height:32 }}>
              <SelectValue placeholder="Tous les groupes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tous les groupes</SelectItem>
              {groups.map(g => (
                <SelectItem key={g.id} value={g.id}>
                  <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ width:9, height:9, borderRadius:'50%', background:g.color, flexShrink:0 }} />
                    {g.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Filtre statut */}
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger style={{ width:150, fontSize:12, height:32 }}>
              <SelectValue placeholder="Tous les statuts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tous les statuts</SelectItem>
              <SelectItem value="yes">Confirmés</SelectItem>
              <SelectItem value="no">Refusés</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="placed">Placés</SelectItem>
              <SelectItem value="unplaced">Non placés</SelectItem>
            </SelectContent>
          </Select>

          <div style={{ flex:1 }} />

          <button onClick={() => setImportModal(true)}
            style={{ ...outlineBtn, display:'flex', alignItems:'center', gap:5 }}>
            <Upload size={13} /> Importer
          </button>
          <button onClick={handleExport}
            style={{ ...outlineBtn, display:'flex', alignItems:'center', gap:5 }}>
            <Download size={13} /> Export CSV
          </button>
          <button onClick={() => setGuestModal({ open:true, guest:null })}
            style={{ ...goldBtn, display:'flex', alignItems:'center', gap:5 }}>
            <Plus size={14} /> Ajouter
          </button>
        </div>

        {/* ─── État vide ─── */}
        {!loading && invites.length === 0 && (
          <div style={{ textAlign:'center', padding:'60px 20px' }}>
            <Users size={48} style={{ color:'#D1D5DB', margin:'0 auto 16px' }} />
            <p style={{ fontWeight:700, fontSize:16, color:'#374151', marginBottom:8 }}>
              Aucun invité pour l'instant
            </p>
            <p style={{ fontSize:13, color:'#9CA3AF', marginBottom:20 }}>
              Ajoutez vos invités un par un ou importez une liste
            </p>
            <div style={{ display:'flex', justifyContent:'center', gap:10 }}>
              <button onClick={() => setGuestModal({ open:true, guest:null })} style={goldBtn}>+ Ajouter</button>
              <button onClick={() => setImportModal(true)} style={outlineBtn}>📥 Importer une liste</button>
            </div>
          </div>
        )}

        {/* ─── Table desktop ─── */}
        {!loading && invites.length > 0 && (
          <div className="guests-table-desktop">
            <div style={{ background:'#FFFFFF', borderRadius:12, border:'1px solid #F3F4F6', overflow:'hidden' }}>
              <Table>
                <TableHeader>
                  <TableRow style={{ background:'#F9FAFB' }}>
                    <TableHead style={{ width:14, padding:'10px 8px' }} />
                    <TableHead style={{ fontSize:11, fontWeight:700, color:'#6B7280', padding:'10px 12px' }}>Nom</TableHead>
                    <TableHead style={{ fontSize:11, fontWeight:700, color:'#6B7280' }}>Groupe</TableHead>
                    {segments.map(seg => (
                      <TableHead key={seg.id} style={{ fontSize:11, fontWeight:700, color:'#6B7280', textAlign:'center' }}>
                        {seg.name}
                      </TableHead>
                    ))}
                    <TableHead style={{ fontSize:11, fontWeight:700, color:'#6B7280' }}>Régime</TableHead>
                    <TableHead style={{ fontSize:11, fontWeight:700, color:'#6B7280' }}>Table</TableHead>
                    <TableHead style={{ width:40 }} />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(inv => {
                    const grp = groupMap[inv.group_id];
                    return (
                      <TableRow key={inv.id}
                        style={{ cursor:'pointer' }}
                        onDoubleClick={() => setGuestModal({ open:true, guest:inv })}>

                        {/* Dot groupe */}
                        <TableCell style={{ padding:'8px 8px' }}>
                          <div style={{ width:10, height:10, borderRadius:'50%',
                            background: grp?.color ?? '#E5E7EB', flexShrink:0 }} />
                        </TableCell>

                        {/* Nom */}
                        <TableCell style={{ padding:'8px 12px' }}>
                          <span style={{ fontWeight:600, fontSize:13, color:'#1F2937' }}>
                            {inv.nom}
                            {inv.age === 'kid'   && <span style={{ fontSize:10, marginLeft:4 }}>👶</span>}
                            {inv.plus_one?.name  && <span style={{ fontSize:10, marginLeft:4, color:'#9CA3AF' }}>+1</span>}
                          </span>
                          {inv.note && <p style={{ fontSize:10, color:'#9CA3AF', margin:0 }}>{inv.note}</p>}
                        </TableCell>

                        {/* Groupe badge */}
                        <TableCell>
                          {grp && (
                            <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:12,
                              background:`${grp.color}22`, color:'#374151' }}>
                              {grp.name}
                            </span>
                          )}
                        </TableCell>

                        {/* Status par segment */}
                        {segments.map(seg => (
                          <TableCell key={seg.id} style={{ textAlign:'center' }}>
                            <StatusDot
                              status={inv.seg_status?.[seg.id] ?? 'pending'}
                              onClick={() => cycleStatus(inv.id, seg.id)}
                              size={24}
                            />
                          </TableCell>
                        ))}

                        {/* Régime */}
                        <TableCell style={{ fontSize:11, color:'#6B7280' }}>
                          {DIET_LABELS[inv.diet] ?? inv.diet}
                        </TableCell>

                        {/* Table assignée */}
                        <TableCell style={{ fontSize:11, color:'#6B7280' }}>
                          {inv.table_id ? '✓' : '—'}
                        </TableCell>

                        {/* Actions */}
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button style={{ background:'none', border:'none', cursor:'pointer',
                                padding:4, borderRadius:4, color:'#9CA3AF', display:'flex', alignItems:'center' }}
                                onClick={e => e.stopPropagation()}>
                                <MoreHorizontal size={15} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" style={{ fontSize:12, minWidth:130 }}>
                              <DropdownMenuItem onClick={() => setGuestModal({ open:true, guest:inv })}>
                                Modifier
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDuplicate(inv)}>
                                Dupliquer
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDelete(inv)}
                                style={{ color:'#EF4444' }}>
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <p style={{ fontSize:11, color:'#9CA3AF', marginTop:8 }}>
              {filtered.length} invité{filtered.length > 1 ? 's' : ''} affiché{filtered.length > 1 ? 's' : ''}
              {filtered.length < invites.length && ` sur ${invites.length}`}
              {' '}· Double-clic sur une ligne pour modifier
            </p>
          </div>
        )}

        {/* ─── Table mobile simplifiée ─── */}
        {!loading && invites.length > 0 && (
          <div className="guests-table-mobile" style={{ display:'none' }}>
            <div style={{ background:'#FFFFFF', borderRadius:12, border:'1px solid #F3F4F6', overflow:'hidden' }}>
              {filtered.map(inv => {
                const grp = groupMap[inv.group_id];
                const dinSeg = segments.find(s => s.has_plan) ?? segments[0];
                return (
                  <div key={inv.id}
                    onClick={() => setGuestModal({ open:true, guest:inv })}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px',
                      borderBottom:'1px solid #F9FAFB', cursor:'pointer' }}>
                    <div style={{ width:11, height:11, borderRadius:'50%',
                      background:grp?.color ?? '#E5E7EB', flexShrink:0 }} />
                    <span style={{ flex:1, fontSize:13, fontWeight:600, color:'#1F2937',
                      whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      {inv.nom}
                    </span>
                    {dinSeg && (
                      <StatusDot
                        status={inv.seg_status?.[dinSeg.id] ?? 'pending'}
                        onClick={e => { e.stopPropagation(); cycleStatus(inv.id, dinSeg.id); }}
                        size={22}
                      />
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button style={{ background:'none', border:'none', cursor:'pointer',
                          padding:4, color:'#9CA3AF' }} onClick={e => e.stopPropagation()}>
                          <MoreHorizontal size={14} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" style={{ fontSize:12 }}>
                        <DropdownMenuItem onClick={() => setGuestModal({ open:true, guest:inv })}>Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(inv)} style={{ color:'#EF4444' }}>Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* CSS responsive */}
      <style>{`
        @media (max-width: 768px) {
          .stats-grid          { grid-template-columns: repeat(3,1fr) !important; }
          .guests-table-desktop { display: none !important; }
          .guests-table-mobile  { display: block !important; }
        }
        @media (max-width: 480px) {
          .stats-grid { grid-template-columns: repeat(2,1fr) !important; }
        }
      `}</style>

      {/* Modals */}
      <GuestModal
        open={guestModal.open}
        onClose={() => setGuestModal({ open:false, guest:null })}
        guest={guestModal.guest}
        groups={groups}
        mainSeg={mainSeg}
        onSave={handleSaveGuest}
      />
      <ImportModal
        open={importModal}
        onClose={() => setImportModal(false)}
        groups={groups}
        onImport={bulkImport}
      />
    </div>
  );
}

const goldBtn    = { padding:'7px 14px', borderRadius:8, border:'none', background:'#D4AF37', color:'#0B1E20', fontWeight:700, fontSize:12, cursor:'pointer', fontFamily:"'Montserrat',sans-serif" };
const outlineBtn = { padding:'6px 12px', borderRadius:8, border:'1px solid #D1D5DB', background:'#FFFFFF', color:'#374151', fontSize:12, cursor:'pointer', fontFamily:"'Montserrat',sans-serif" };
